/* eslint-disable no-unused-vars */

import { EquipmentSelector, SitesSelector } from 'selectors';
import { v4 as uuidV4 } from 'uuid';
import { store } from 'redux/store';
import { dropdownMenuMap } from '@utilities/helper.util';
import { getFormFields, getMoreInfoFormFields } from './form-fields';
import { decodeToken } from '@utilities/authentication';

import {
  addOrEditEquipmentSchema,
  addOrEditEquipmentSchemaForNewEquipment,
  defaultValues,
  defaultValuesForNewEquipment,
  equipmentMoreFieldsSchema
} from './form-schema-and-default-value';
import {
  getEquipmentTypeVersion,
  getEquipmentPSVsInfo,
  getEquipmentVesselsInfo
} from './vessels-psvs-info';
import EquipmentAutoPopulate from '@components/work-order/services/equipment-auto-populate';
import structureAutoPopulate from '@components/work-order/services/structure-auto-populate';

const getEquipmentDetailsWithVesselsAndPSVsAndItsSiteDetails = async ({
  equipmentId,
  siteIdForNewEquipment,
  equipmentIdThatAttachedToPSV,
  vesselEquipmentId
}) => {
  try {
    const { cache, picklists, jurisdictionNumbers, pvPsv, inspection, structure, settings } =
      store.getState();
    const { equipmentsMap = {}, equipments: cacheEquipments = [] } = cache;
    const { jurisdictionNumbersMap = {} } = jurisdictionNumbers;
    const { pvPsvMap = {} } = pvPsv;
    const { picklistsMap = {} } = picklists;
    const equipmentDetails = cacheEquipments[equipmentsMap[equipmentId]] || {};
    const siteId = equipmentDetails?.siteId || siteIdForNewEquipment || null;
    const structureId = equipmentDetails?.structureId;

    const { pv = [], psv = [] } = pvPsvMap[equipmentId] || {};
    const { equipmentFormsUserCache = [], equipmentFormsUserCacheMap = {} } = inspection;

    const siteDetails = siteId
      ? await SitesSelector.getSitesWithAllTasks({
          id: siteId,
          store: store.getState()
        })
      : [];

    const dropdownMenuMapList = dropdownMenuMap({
      inspection
    });

    const equipmentCategory = inspection?.equipmentCategory || [];
    const equipmentCategoryMap = equipmentCategory?.reduce((acc, curr) => {
      acc[curr.value] = curr;
      return acc;
    }, {});

    const { isVesselVersion, isPSVVersion } = getEquipmentTypeVersion({
      equipmentCategoryMap,
      equipmentDetails,
      equipmentIdThatAttachedToPSV
    });

    const { moreInfoOfVessel, attachAndDeAttach } = getEquipmentVesselsInfo({
      equipmentId,
      equipmentsMap,
      cacheEquipments,
      pv,
      picklistsMap,
      jurisdictionNumbersMap,
      pvIds: equipmentDetails?.pvIds || [],
      vesselEquipmentId
      // siteIdForNewEquipment
    });

    const moreInfoOfPSVs = getEquipmentPSVsInfo({
      equipmentId,
      equipmentsMap,
      cacheEquipments,
      psv,
      picklistsMap,
      jurisdictionNumbersMap,
      settings
      // siteIdForNewEquipment: siteIdForNewEquipment
    });

    const formFields = getFormFields({
      isNewEquipment: siteIdForNewEquipment || equipmentDetails?.isNew ? true : false,
      dropdownMenuMapList,
      isVesselVersion: isVesselVersion && moreInfoOfPSVs?.length > 0,
      isPSVVersion: isPSVVersion
    });

    const { moreInfoFormFields } = getMoreInfoFormFields({
      equipmentFormsUserCache,
      dropdownMenuMapList,
      isPSVVersion,
      selectedFieldObjIds: equipmentDetails?.selectedFieldObjIds || [],
      isNewEquipment: siteIdForNewEquipment || equipmentDetails?.isNew ? true : false
    });

    const initialFormFieldsValue =
      siteIdForNewEquipment && !equipmentDetails
        ? {}
        : setDefaultValuesOnAddOrEditEquipmentForm({
            equipmentDetails,
            jurisdictionNumbersMap,
            picklistsMap
          });

    const formWrapperSchema =
      siteIdForNewEquipment || equipmentDetails?.isNew
        ? addOrEditEquipmentSchemaForNewEquipment
        : addOrEditEquipmentSchema;

    const equipmentMoreFieldSchema = equipmentMoreFieldsSchema({ moreInfoFormFields });

    const formWrapperDefaultValues =
      siteIdForNewEquipment || equipmentDetails?.isNew
        ? defaultValuesForNewEquipment
        : {
            ...defaultValues,
            ...(isVesselVersion && {
              psv: [{ ...defaultValues }]
            }),
            selectedMoreFields: []
          };

    const { structureMobileMap = {}, structureMobile = [] } = structure;
    const checkSheetStructureData =
      (structureId && structureMobile[structureMobileMap[structureId]]) || {};

    let equipmentAutoPopulateFields = EquipmentAutoPopulate({
      intervalId: null,
      firstName: decodeToken().firstName,
      lastName: decodeToken().lastName,
      equipment: equipmentDetails,
      jurisdictionNumber: null,
      jurisdiction: null,
      inspectionLastDate: []
    });

    const structureAutoPopulateFields = structureAutoPopulate({
      intervalId: null,
      firstName: decodeToken().firstName,
      lastName: decodeToken().lastName,
      structure: checkSheetStructureData
    });

    equipmentAutoPopulateFields = {
      Equipment: {
        ...equipmentAutoPopulateFields['Equipment']
      },
      Structure: {
        ...structureAutoPopulateFields['Structure']
      }
    };

    return {
      siteDetail: siteDetails || {},
      formFields,
      moreInfoFormFields,
      initialFormFieldsValue: {
        ...initialFormFieldsValue,
        ...(isVesselVersion && {
          psv: moreInfoOfPSVs
        }),
        ...(isPSVVersion && {
          vessel: moreInfoOfVessel
        }),
        selectedMoreFields: moreInfoFormFields,
        ...(equipmentDetails?.moreFields && {
          moreFields: equipmentDetails?.moreFields
        })
      },
      moreInfoOfVessel,
      moreInfoOfPSVs,
      equipmentDetails,
      formWrapperSchema,
      equipmentMoreFieldSchema,
      formWrapperDefaultValues,
      isVesselVersion,
      isPSVVersion,
      equipmentCategoryMap,
      equipmentAutoPopulateFields,
      attachAndDeAttach
    };
  } catch (error) {
    console.log({ error });
  }
};

export const setDefaultValuesOnAddOrEditEquipmentForm = ({
  equipmentDetails,
  jurisdictionNumbersMap,
  picklistsMap,
  isPSVs = false,
  pvId = ''
}) => {
  const {
    jurisdictionNumber,
    jurisdiction,
    reconcile = {}
  } = jurisdictionNumbersMap[equipmentDetails.equipmentId] || {};
  // console.log(
  //   { jurisdictionNumber, jurisdiction },
  //   jurisdictionNumbersMap[equipmentDetails.equipmentId]
  // );

  const equipmentCategoryTypeAndSubtype =
    equipmentDetails?.equipmentCategory &&
    equipmentDetails?.equipmentType &&
    equipmentDetails?.equipmentSubType
      ? equipmentDetails?.equipmentSubType
      : equipmentDetails?.equipmentCategory && equipmentDetails?.equipmentType
      ? equipmentDetails?.equipmentType
      : equipmentDetails?.equipmentCategory
      ? equipmentDetails?.equipmentCategory
      : '';

  const equipmentAlreadyUpdated =
    equipmentDetails?.equipmentEditingStatus &&
    equipmentDetails?.reconcile?.name &&
    ['Pending sync', 'In progress'].includes(equipmentDetails?.equipmentEditingStatus)
      ? true
      : false;

  let pvIds = [];
  let verificationStatus = '';
  if (isPSVs) {
    pvIds = equipmentDetails?.pvIds?.length > 0 ? equipmentDetails?.pvIds : [];

    const findIndex = pvIds?.findIndex((x) => x?.pvId === pvId);
    if (findIndex >= 0) {
      verificationStatus = pvIds[findIndex]?.verificationStatus || '';
    } else {
      verificationStatus = equipmentDetails?.isNew ? 'new' : 'attached';
      pvIds = [
        ...pvIds,
        {
          pvId: pvId,
          psvId: equipmentDetails?.equipmentId,
          verificationStatus
        }
      ];
    }
  }

  return {
    commentForReviewer: equipmentDetails?.commentForReviewer || '',
    equipmentNumber: equipmentDetails?.equipmentNumber || '',
    name: equipmentDetails?.name || '',
    status: equipmentDetails?.status || '',
    equipmentCategoryTypeSubType: equipmentCategoryTypeAndSubtype || '',
    description: equipmentDetails?.description || '',
    manufacturer: equipmentDetails?.manufacturer || '',
    model: equipmentDetails?.model || '',
    physicalLocation: equipmentDetails?.physicalLocation || '',
    serial: equipmentDetails?.serialNumber || '',
    jurisdiction: jurisdiction || '',
    jurisdictionNumber: jurisdictionNumber || '',
    fieldTagNumber: equipmentDetails?.fieldTagNumber || '',
    manufacturedYear: equipmentDetails?.manufacturedYear || '',
    isNew: equipmentDetails?.isNew || false,
    verificationStatus,
    equipmentCategoryValue: picklistsMap[equipmentDetails?.equipmentCategory]?.value || '',
    equipmentTypeValue: picklistsMap[equipmentDetails?.equipmentType]?.value,
    equipmentId: equipmentDetails?.equipmentId || '',
    link: equipmentDetails?.link || '',
    equipmentEditingStatus: equipmentDetails?.equipmentEditingStatus || '',
    isLinkToExistingPSV: equipmentDetails?.isLinkToExistingPSV || false,
    pvIds,

    'name-reconcile': equipmentAlreadyUpdated
      ? equipmentDetails?.reconcile?.name
      : equipmentDetails?.name || '',

    'status-reconcile': equipmentAlreadyUpdated
      ? equipmentDetails?.reconcile?.status
      : equipmentDetails?.status || '',

    'equipmentCategoryTypeSubType-reconcile': equipmentAlreadyUpdated
      ? equipmentDetails?.reconcile?.equipmentCategoryTypeSubType
      : equipmentCategoryTypeAndSubtype || '',

    'description-reconcile': equipmentAlreadyUpdated
      ? equipmentDetails?.reconcile?.description
      : equipmentDetails?.description || '',

    'manufacturer-reconcile': equipmentAlreadyUpdated
      ? equipmentDetails?.reconcile?.manufacturer
      : equipmentDetails?.manufacturer || '',

    'model-reconcile': equipmentAlreadyUpdated
      ? equipmentDetails?.reconcile?.model
      : equipmentDetails?.model || '',

    'physicalLocation-reconcile': equipmentAlreadyUpdated
      ? equipmentDetails?.reconcile?.physicalLocation
      : equipmentDetails?.physicalLocation || '',

    'serial-reconcile': equipmentAlreadyUpdated
      ? equipmentDetails?.reconcile?.serialNumber
      : equipmentDetails?.serialNumber || '',

    'jurisdiction-reconcile': equipmentAlreadyUpdated
      ? reconcile?.jurisdiction
        ? reconcile?.jurisdiction
        : ''
      : jurisdiction || '',
    'jurisdictionNumber-reconcile': equipmentAlreadyUpdated
      ? reconcile?.jurisdictionNumber
        ? reconcile?.jurisdictionNumber
        : ''
      : jurisdictionNumber || '',

    'fieldTagNumber-reconcile': equipmentAlreadyUpdated
      ? equipmentDetails?.reconcile?.fieldTagNumber
      : equipmentDetails?.fieldTagNumber || '',
    'manufacturedYear-reconcile': equipmentAlreadyUpdated
      ? equipmentDetails?.reconcile?.manufacturedYear
      : equipmentDetails?.manufacturedYear || ''
  };
};

const transformEditEquipmentDataForUpdate = ({ data, equipmentId }) => {
  const { inspection, jurisdictionNumbers } = store.getState();
  const { jurisdictionNumbersMap = {} } = jurisdictionNumbers;

  const equipmentCategoryTypeSubtype = inspection.equipmentCategoryTypeSubtype || [];
  const equipmentCategoryKeys = getParentKeys(
    equipmentCategoryTypeSubtype,
    data.equipmentCategoryTypeSubType
  );
  const equipmentCategory = equipmentCategoryKeys[0] || '';
  const equipmentType = equipmentCategoryKeys[1] || '';
  const equipmentSubType = equipmentCategoryKeys[2] || '';

  const transform = {
    equipmentNumber: data?.equipmentNumber || '',
    name: data?.name || '',
    status: data?.status || '',
    equipmentCategory,
    equipmentType,
    equipmentSubType,
    description: data?.description || '',
    manufacturer: data?.manufacturer || '',
    model: data?.model || '',
    physicalLocation: data?.physicalLocation || '',
    serialNumber: data?.serial || '',
    fieldTagNumber: data?.fieldTagNumber || '',
    manufacturedYear: data?.manufacturedYear || '',
    commentForReviewer: data?.commentForReviewer || '',
    equipmentCategoryTypeSubType: data?.equipmentCategoryTypeSubType || '',
    reconcile: {
      name: data?.['name-reconcile'] || '',
      status: data?.['status-reconcile'] || '',
      equipmentCategoryTypeSubType: data?.['equipmentCategoryTypeSubType-reconcile'] || '',
      description: data?.['description-reconcile'] || '',
      manufacturer: data?.['manufacturer-reconcile'] || '',
      model: data?.['model-reconcile'] || '',
      physicalLocation: data?.['physicalLocation-reconcile'] || '',
      serialNumber: data?.['serial-reconcile'] || '',
      fieldTagNumber: data?.['fieldTagNumber-reconcile'] || '',
      manufacturedYear: data?.['manufacturedYear-reconcile'] || ''
    },

    moreFields: data?.moreFields || {},
    selectedFieldObjIds: data?.selectedFieldObjIds || []
  };

  let newJurisdictionNumber = getUpdatedJurisdictionNumberListAndMap({
    equipmentId,
    jurisdictionNumbersMap,
    jurisdictionNumbers,
    data
  });

  return { transform, newJurisdictionNumber };
};

const transformAddOrEditNewEquipmentDataForUpdate = ({ data, equipmentId }) => {
  const { inspection, jurisdictionNumbers } = store.getState();
  const { jurisdictionNumbersMap = {} } = jurisdictionNumbers;

  const equipmentCategoryTypeSubtype = inspection.equipmentCategoryTypeSubtype || [];
  const equipmentCategoryKeys = getParentKeys(
    equipmentCategoryTypeSubtype,
    data.equipmentCategoryTypeSubType
  );
  const equipmentCategory = equipmentCategoryKeys[0] || '';
  const equipmentType = equipmentCategoryKeys[1] || '';
  const equipmentSubType = equipmentCategoryKeys[2] || '';

  const transform = {
    // equipmentNumber: data?.equipmentNumber || '',
    name: data?.name || '',
    status: data?.status || '',
    equipmentCategory,
    equipmentType,
    equipmentSubType,
    description: data?.description || '',
    manufacturer: data?.manufacturer || '',
    model: data?.model || '',
    physicalLocation: data?.physicalLocation || '',
    serialNumber: data?.serial || '',
    fieldTagNumber: data?.fieldTagNumber || '',
    manufacturedYear: data?.manufacturedYear || '',
    commentForReviewer: data?.commentForReviewer || '',
    equipmentCategoryTypeSubType: data?.equipmentCategoryTypeSubType || '',
    moreFields: data?.moreFields || {},
    selectedFieldObjIds: data?.selectedFieldObjIds || []
  };

  let newJurisdictionNumber = getUpdatedJurisdictionNumberListAndMap({
    equipmentId,
    jurisdictionNumbersMap,
    jurisdictionNumbers,
    data,
    newEquipment: true
  });

  return { transform, newJurisdictionNumber };
};

const revertEditEquipmentData = ({ equipmentId }) => {
  const { cache, jurisdictionNumbers, inspection } = store.getState();
  const { equipmentsMap = {}, equipments: cacheEquipments = [] } = cache;
  const { jurisdictionNumbersMap = {} } = jurisdictionNumbers;

  const equipmentDetails = cacheEquipments[equipmentsMap[equipmentId]] || {};
  const originalEquipmentData = equipmentDetails.reconcile?.name
    ? equipmentDetails.reconcile
    : null;

  if (originalEquipmentData) {
    const equipmentCategoryTypeSubtype = inspection.equipmentCategoryTypeSubtype || [];
    const equipmentCategoryKeys = getParentKeys(
      equipmentCategoryTypeSubtype,
      originalEquipmentData?.equipmentCategoryTypeSubType
    );
    const equipmentCategory = equipmentCategoryKeys[0] || '';
    const equipmentType = equipmentCategoryKeys[1] || '';
    const equipmentSubType = equipmentCategoryKeys[2] || '';
    const transform = {
      name: originalEquipmentData?.name || '',
      status: originalEquipmentData?.status || '',
      equipmentCategory,
      equipmentType,
      equipmentSubType,
      description: originalEquipmentData?.description || '',
      manufacturer: originalEquipmentData?.manufacturer || '',
      model: originalEquipmentData?.model || '',
      physicalLocation: originalEquipmentData?.physicalLocation || '',
      serial: originalEquipmentData?.serial || '',
      fieldTagNumber: originalEquipmentData?.fieldTagNumber || '',
      manufacturedYear: originalEquipmentData?.manufacturedYear || '',
      commentForReviewer: '',
      equipmentCategoryTypeSubType: '',
      reconcile: null,
      moreFields: {},
      selectedFieldObjIds: []
    };

    const transformJurisdiction = revertUpdatedJurisdictionNumbersListAndMap({
      equipmentId,
      jurisdictionNumbersMap,
      jurisdictionNumbers
    });
    return { transform, transformJurisdiction };
  } else {
    const transform = {
      ...equipmentDetails
    };

    const transformJurisdiction = revertUpdatedJurisdictionNumbersListAndMap({
      equipmentId,
      jurisdictionNumbersMap,
      jurisdictionNumbers
    });
    return { transform, transformJurisdiction };
  }
};

const revertNewEquipmentJurisdictionNumbersListAndMap = ({ equipmentId }) => {
  const { jurisdictionNumbers } = store.getState();
  const { jurisdictionNumbersMap = {} } = jurisdictionNumbers;
  const transformJurisdiction = revertUpdatedJurisdictionNumbersListAndMap({
    equipmentId,
    jurisdictionNumbersMap,
    jurisdictionNumbers
  });
  return transformJurisdiction;
};

export {
  getEquipmentDetailsWithVesselsAndPSVsAndItsSiteDetails,
  transformEditEquipmentDataForUpdate,
  revertEditEquipmentData,
  transformAddOrEditNewEquipmentDataForUpdate,
  revertNewEquipmentJurisdictionNumbersListAndMap
};

export const getParentKeys = (data, targetKey, parentKeys = []) => {
  for (const item of data) {
    if (item.key === targetKey) {
      parentKeys.push(item.key);
      break;
    } else if (item.child && item.child.length > 0) {
      parentKeys.push(item.key);
      getParentKeys(item.child, targetKey, parentKeys);
      if (parentKeys.includes(targetKey)) {
        break;
      } else {
        parentKeys.pop();
      }
    }
  }
  return parentKeys;
};

const getUpdatedJurisdictionNumberListAndMap = ({
  data,
  jurisdictionNumbersMap,
  jurisdictionNumbers,
  equipmentId,
  newEquipment = false
}) => {
  let transformJurisdiction = !newEquipment
    ? !data?.['jurisdiction-reconcile'] &&
      !data?.['jurisdictionNumber-reconcile'] &&
      !data?.jurisdiction &&
      !data?.jurisdictionNumber
      ? null
      : {
          jurisdiction: data?.jurisdiction || '',
          jurisdictionNumber: data?.jurisdictionNumber || '',
          reconcile: {
            jurisdiction: data?.['jurisdiction-reconcile'] || '',
            jurisdictionNumber: data?.['jurisdictionNumber-reconcile'] || ''
          }
        }
    : !data?.jurisdiction && !data?.jurisdictionNumber
    ? null
    : {
        jurisdiction: data?.jurisdiction || '',
        jurisdictionNumber: data?.jurisdictionNumber || ''
      };

  if (transformJurisdiction) {
    const copy = jurisdictionNumbersMap ? { ...jurisdictionNumbersMap } : {};
    if (!copy[equipmentId]) {
      transformJurisdiction = { ...transformJurisdiction, isNew: true, isEdit: true };

      let newJurisdictionNumbersList = [...jurisdictionNumbers.jurisdictionNumbers] || [];
      let newIJurisdictionNumberId = uuidV4();
      newJurisdictionNumbersList.push({
        jurisdictionNumberId: newIJurisdictionNumberId,
        equipmentId: equipmentId,
        jurisdiction: data?.jurisdiction || '',
        jurisdictionNumber: data?.jurisdictionNumber || '',
        isPrimary: false,
        isNew: true
      });

      let newJurisdictionNumbersMap = newJurisdictionNumbersList.reduce((acc, curr) => {
        acc[curr.equipmentId] = curr;
        return acc;
      }, {});

      newJurisdictionNumbersMap[equipmentId] = {
        ...newJurisdictionNumbersMap[equipmentId],
        ...transformJurisdiction,
        equipmentId,
        isEdit: true
      };

      return {
        jurisdictionNumbersMap: newJurisdictionNumbersMap,
        jurisdictionNumbers: newJurisdictionNumbersList
      };
    } else {
      // exists already then update jurisdiction

      let newJurisdictionNumbersList = [...jurisdictionNumbers.jurisdictionNumbers] || [];
      const index = newJurisdictionNumbersList.findIndex((x) => x?.equipmentId === equipmentId);
      newJurisdictionNumbersList[index] = {
        ...newJurisdictionNumbersList[index],
        jurisdiction: transformJurisdiction?.jurisdiction || '',
        jurisdictionNumber: transformJurisdiction?.jurisdictionNumber || '',
        isPrimary: false,
        isEdit: true
      };

      const copy = { ...jurisdictionNumbersMap };
      const newData = {
        ...copy[equipmentId],
        ...transformJurisdiction,
        equipmentId,
        isEdit: true
      };

      copy[equipmentId] = { ...newData };

      return {
        jurisdictionNumbersMap: copy,
        jurisdictionNumbers: newJurisdictionNumbersList || []
      };
    }
  } else {
    return null;
  }
};

export const revertUpdatedJurisdictionNumbersListAndMap = ({
  jurisdictionNumbersMap,
  jurisdictionNumbers,
  equipmentId
}) => {
  let equipmentJurisdictionNumber = jurisdictionNumbersMap[equipmentId] || {};
  if (equipmentJurisdictionNumber) {
    if (equipmentJurisdictionNumber?.isNew) {
      let newJurisdictionNumbersList = [...jurisdictionNumbers.jurisdictionNumbers] || [];
      newJurisdictionNumbersList = newJurisdictionNumbersList?.filter(
        (x) => x?.equipmentId != equipmentId
      );

      let newJurisdictionNumbersMap = newJurisdictionNumbersList.reduce((acc, curr) => {
        acc[curr.equipmentId] = curr;
        return acc;
      }, {});

      return {
        jurisdictionNumbersMap: newJurisdictionNumbersMap,
        jurisdictionNumbers: newJurisdictionNumbersList
      };
    } else if (equipmentJurisdictionNumber?.isEdit) {
      let newJurisdictionNumbersList = [...jurisdictionNumbers.jurisdictionNumbers] || [];

      let newJurisdictionNumbersMap = { ...jurisdictionNumbersMap };
      const originalValue = newJurisdictionNumbersMap[equipmentId]?.reconcile || {};
      newJurisdictionNumbersMap[equipmentId] = {
        ...newJurisdictionNumbersMap[equipmentId],
        jurisdiction: originalValue?.jurisdiction || '',
        jurisdictionNumber: originalValue?.jurisdictionNumber || '',
        isEdit: false,
        isNew: false,
        reconcile: null
      };
      const index = newJurisdictionNumbersList?.findIndex((x) => x?.equipmentId === equipmentId);
      newJurisdictionNumbersList[index] = {
        ...newJurisdictionNumbersMap[equipmentId],
        jurisdiction: originalValue?.jurisdiction || '',
        jurisdictionNumber: originalValue?.jurisdictionNumber || '',
        isEdit: false,
        isNew: false,
        reconcile: null
      };

      return {
        jurisdictionNumbersMap: newJurisdictionNumbersMap,
        jurisdictionNumbers: newJurisdictionNumbersList
      };
    }
  } else {
    return null;
  }
};

export const attachedNewPSVToVesselEquipment = ({ equipmentId, attachedPSVId }) => {
  try {
    const { pvPsv } = store.getState();

    let copyPvPsv = [...pvPsv.pvPsv] || [];
    // copyPvPsv = copyPvPsv?.filter((x) => !x?.isNew);

    const isAlreadyExists = copyPvPsv?.filter(
      (x) => x?.psvId === attachedPSVId && x?.pvId === equipmentId
    );
    if (isAlreadyExists?.length > 0) {
      return {
        pvPsv: copyPvPsv,
        pvPsvMap: pvPsv.pvPsvMap,
        alreadyExists: true
      };
    } else {
      copyPvPsv.push({
        pvId: equipmentId,
        psvId: attachedPSVId,
        isNew: true
      });

      const pvPsvMap = copyPvPsv?.reduce((acc, { pvId, psvId }) => {
        if (!acc[pvId]) acc[pvId] = { pv: [], psv: [] }; // for uniqueness
        if (!acc[psvId]) acc[psvId] = { pv: [], psv: [] }; // for uniqueness
        acc[pvId].psv.push(psvId);
        acc[psvId].pv.push(pvId);
        return acc;
      }, {});

      return {
        pvPsv: copyPvPsv,
        pvPsvMap,
        alreadyExists: false
      };
    }
  } catch (error) {
    console.log({ error });
  }
};

export const revertPSVs = ({ psvs, equipmentId }) => {
  try {
    let updatesPSVs = []; // already assigned PSVS
    let deleteNewPSVsIds = []; // new created PSVS IDs
    let linkPSVsIds = []; //new attached link psvs ids
    for (let psv of psvs) {
      if (psv.isNew) {
        deleteNewPSVsIds.push(psv.equipmentId);
      } else if (psv?.equipmentEditingStatus || psv?.isLinkToExistingPSV) {
        const { transform, transformJurisdiction } = revertEditEquipmentData({
          equipmentId: psv?.equipmentId
        });

        const { cache } = store.getState();
        const { equipmentsMap = {}, equipments: cacheEquipments = [] } = cache;
        const equipmentDetails = cacheEquipments[equipmentsMap[psv?.equipmentId]] || {};
        let pvIds = equipmentDetails?.pvIds || [];
        pvIds = pvIds?.filter((x) => x?.pvId !== equipmentId) || [];

        updatesPSVs.push({
          transformJurisdiction,
          transform: {
            ...equipmentDetails,
            // ...transform,
            equipmentId: psv?.equipmentId,
            equipmentEditingStatus: equipmentDetails?.equipmentEditingStatus,
            pvIds: pvIds
          },
          unLink: psv?.isLinkToExistingPSV ? true : false
        });
        psv?.isLinkToExistingPSV && linkPSVsIds.push(psv.equipmentId);
      }
    }
    return {
      updatesPSVs,
      deleteNewPSVsIds,
      linkPSVsIds
    };
  } catch (error) {
    console.log({ error });
  }
};
